import { useSelector } from '@xstate/store/react';
import { useMemo, useCallback, useRef, useSyncExternalStore } from 'react';
import { uiStore } from './uiStore';
import { skillsStore, generateSkillId } from './skillsStore';
import { globalRequirementsStore } from './globalRequirementsStore';
import { boardsStore } from './boardsStore';
import { mainNavStore, type NavStage } from './mainNavStore';
import type { SkillDefinition } from '../skills';
import type { GlobalRequirement, BoardDefinition } from '../types';
import type { ActiveDrag } from './types';

// Re-export board hooks for backwards compatibility
export {
  useMaterializedTasks,
  useTask,
  useAllTasks,
  useColumnTasks,
  useColumns,
  useColumnNames,
  useBoardStartDate,
  useColumnsActions,
  useScheduledTask,
  useScheduledTasks,
  useEmployeeStats,
  useEmployeeStatsById,
  useDoubleBookingWarnings,
  useIsScheduleReady,
  useBoardRequirements,
  useBoardRequirementsActions,
  useRoadmapActions,
  useExportData,
} from '../boardv3/hooks/useBoardHooks';

// ============================================
// UI Hooks
// ============================================

export function useActiveDrag(): ActiveDrag {
  return useSelector(
    uiStore,
    (state) => state.context.activeDrag
  );
}

export function useDragOverColumn(): string | null {
  const cachedRef = useRef<string | null>(null);

  const subscribe = useCallback((onStoreChange: () => void) => {
    const subscription = uiStore.subscribe(onStoreChange);
    return typeof subscription === 'function' ? subscription : subscription.unsubscribe;
  }, []);

  const getSnapshot = useCallback(() => {
    const newValue = uiStore.getSnapshot().context.dragOverColumn;
    if (cachedRef.current === newValue) return cachedRef.current;
    cachedRef.current = newValue;
    return newValue;
  }, []);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function useSelectedTaskId(): string | null {
  const cachedRef = useRef<string | null>(null);

  const subscribe = useCallback((onStoreChange: () => void) => {
    const subscription = uiStore.subscribe(onStoreChange);
    return typeof subscription === 'function' ? subscription : subscription.unsubscribe;
  }, []);

  const getSnapshot = useCallback(() => {
    const newValue = uiStore.getSnapshot().context.selectedTaskId;
    if (cachedRef.current === newValue) return cachedRef.current;
    cachedRef.current = newValue;
    return newValue;
  }, []);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function useSelectedSkillId(): string | null {
  const cachedRef = useRef<string | null>(null);

  const subscribe = useCallback((onStoreChange: () => void) => {
    const subscription = uiStore.subscribe(onStoreChange);
    return typeof subscription === 'function' ? subscription : subscription.unsubscribe;
  }, []);

  const getSnapshot = useCallback(() => {
    const newValue = uiStore.getSnapshot().context.selectedSkillId;
    if (cachedRef.current === newValue) return cachedRef.current;
    cachedRef.current = newValue;
    return newValue;
  }, []);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function useIsTaskSelected(taskId: string): boolean {
  const cachedRef = useRef<boolean>(false);

  const subscribe = useCallback((onStoreChange: () => void) => {
    const subscription = uiStore.subscribe(onStoreChange);
    return typeof subscription === 'function' ? subscription : subscription.unsubscribe;
  }, []);

  const getSnapshot = useCallback(() => {
    const isSelected = uiStore.getSnapshot().context.selectedTaskId === taskId;
    if (cachedRef.current === isSelected) return cachedRef.current;
    cachedRef.current = isSelected;
    return isSelected;
  }, [taskId]);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function useZoomLevel(): number {
  return useSelector(uiStore, (state) => state.context.zoomLevel);
}

export function useShowImportModal(): boolean {
  return useSelector(uiStore, (state) => state.context.showImportModal);
}

export function useImportText(): string {
  return useSelector(uiStore, (state) => state.context.importText);
}

export function useImportError(): string | null {
  return useSelector(uiStore, (state) => state.context.importError);
}

export function useContextMenu() {
  return useSelector(uiStore, (state) => state.context.contextMenu);
}

export function useCompactView(): boolean {
  return useSelector(uiStore, (state) => state.context.compactView);
}

export function useUIActions() {
  return useMemo(() => ({
    startDrag: (drag: ActiveDrag) =>
      uiStore.send({ type: 'drag.start', drag }),
    moveDrag: (overColumn: string | null) =>
      uiStore.send({ type: 'drag.move', overColumn }),
    endDrag: () =>
      uiStore.send({ type: 'drag.end' }),
    selectTask: (taskId: string | null) =>
      uiStore.send({ type: 'task.select', taskId }),
    selectSkill: (skillId: string | null) =>
      uiStore.send({ type: 'skill.select', skillId }),
    zoomIn: () =>
      uiStore.send({ type: 'zoom.in' }),
    zoomOut: () =>
      uiStore.send({ type: 'zoom.out' }),
    zoomReset: () =>
      uiStore.send({ type: 'zoom.reset' }),
    setZoom: (level: number) =>
      uiStore.send({ type: 'zoom.set', level }),
    openImportModal: () =>
      uiStore.send({ type: 'modal.import.open' }),
    closeImportModal: () =>
      uiStore.send({ type: 'modal.import.close' }),
    setImportText: (text: string) =>
      uiStore.send({ type: 'modal.import.setText', text }),
    setImportError: (error: string | null) =>
      uiStore.send({ type: 'modal.import.setError', error }),
    openContextMenu: (x: number, y: number, taskId: string) =>
      uiStore.send({ type: 'contextMenu.open', x, y, taskId }),
    closeContextMenu: () =>
      uiStore.send({ type: 'contextMenu.close' }),
    toggleCompactView: () =>
      uiStore.send({ type: 'compactView.toggle' }),
    resetUI: () =>
      uiStore.send({ type: 'ui.reset' }),
  }), []);
}

// ============================================
// Skills Hooks
// ============================================

export function useSkills(): SkillDefinition[] {
  const cachedRef = useRef<SkillDefinition[]>([]);

  const subscribe = useCallback((onStoreChange: () => void) => {
    const subscription = skillsStore.subscribe(onStoreChange);
    return typeof subscription === 'function' ? subscription : subscription.unsubscribe;
  }, []);

  const getSnapshot = useCallback(() => {
    const newSkills = skillsStore.getSnapshot().context.skills;
    if (cachedRef.current.length === newSkills.length &&
        cachedRef.current.every((s, i) => s.id === newSkills[i]?.id)) {
      return cachedRef.current;
    }
    cachedRef.current = newSkills;
    return newSkills;
  }, []);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function useSkillById(skillId: string): SkillDefinition | undefined {
  const skills = useSkills();
  return useMemo(() => skills.find(s => s.id === skillId), [skills, skillId]);
}

export function useSkillsActions() {
  return useMemo(() => ({
    addSkill: (skill: SkillDefinition) =>
      skillsStore.send({ type: 'skill.add', skill }),
    addNewSkill: (name: string, emoji: string, description: string) => {
      const id = generateSkillId(name);
      skillsStore.send({ type: 'skill.add', skill: { id, name, emoji, description } });
      return id;
    },
    updateSkill: (skillId: string, updates: Partial<Omit<SkillDefinition, 'id'>>) =>
      skillsStore.send({ type: 'skill.update', skillId, updates }),
    deleteSkill: (skillId: string) =>
      skillsStore.send({ type: 'skill.delete', skillId }),
    resetSkills: () =>
      skillsStore.send({ type: 'skills.reset' }),
    importSkills: (skills: SkillDefinition[]) =>
      skillsStore.send({ type: 'skills.import', skills }),
  }), []);
}

// Employees Hooks (re-exported from module)
export { useEmployees, useEmployeeById, useEmployeesActions } from '../employees/hooks';

// Teams Hooks (re-exported from module)
export { useTeams, useTeamById, useTeamsActions } from '../teams/hooks';

// Task Categories Hooks (re-exported from module)
export { useTaskCategories, useTaskCategoryById, useTaskCategoriesActions } from '../taskCategories/hooks';

// ============================================
// Global Requirements Hooks
// ============================================

export function useGlobalRequirements(): GlobalRequirement[] {
  const cachedRef = useRef<GlobalRequirement[]>([]);

  const subscribe = useCallback((onStoreChange: () => void) => {
    const subscription = globalRequirementsStore.subscribe(onStoreChange);
    return typeof subscription === 'function' ? subscription : subscription.unsubscribe;
  }, []);

  const getSnapshot = useCallback(() => {
    const newRequirements = globalRequirementsStore.getSnapshot().context.requirements;
    if (cachedRef.current.length === newRequirements.length &&
        cachedRef.current.every((r, i) => {
          const n = newRequirements[i];
          return n && r.text === n.text && r.type === n.type && r.description === n.description;
        })) {
      return cachedRef.current;
    }
    cachedRef.current = newRequirements;
    return newRequirements;
  }, []);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function useGlobalRequirementsActions() {
  return useMemo(() => ({
    addRequirement: (requirement: GlobalRequirement) =>
      globalRequirementsStore.send({ type: 'globalRequirement.add', requirement }),
    addNewRequirement: (text: string, type: 'hard' | 'soft', description: string) => {
      globalRequirementsStore.send({ type: 'globalRequirement.add', requirement: { text, type, description } });
    },
    updateRequirement: (text: string, updates: Partial<GlobalRequirement>) =>
      globalRequirementsStore.send({ type: 'globalRequirement.update', text, updates }),
    deleteRequirement: (text: string) =>
      globalRequirementsStore.send({ type: 'globalRequirement.delete', text }),
    resetRequirements: () =>
      globalRequirementsStore.send({ type: 'globalRequirements.reset' }),
    importRequirements: (requirements: GlobalRequirement[]) =>
      globalRequirementsStore.send({ type: 'globalRequirements.import', requirements }),
  }), []);
}

// ============================================
// Boards Hooks
// ============================================

export function useBoards(): BoardDefinition[] {
  return useSelector(boardsStore, (state) => state.context.boards);
}

export function useActiveBoardIdFromStore(): string | null {
  return useSelector(boardsStore, (state) => state.context.activeBoardId);
}

export function useBoardsActions() {
  return useMemo(() => ({
    createBoard: (board: BoardDefinition) =>
      boardsStore.send({ type: 'board.create', board }),
    updateBoard: (boardId: string, updates: Partial<Omit<BoardDefinition, 'id' | 'createdAt'>>) =>
      boardsStore.send({ type: 'board.update', boardId, updates }),
    deleteBoard: (boardId: string) =>
      boardsStore.send({ type: 'board.delete', boardId }),
    setActiveBoard: (boardId: string) =>
      boardsStore.send({ type: 'board.setActive', boardId }),
  }), []);
}

// ============================================
// Main Nav Hooks
// ============================================

export function useMainNavStage(): NavStage {
  return useSelector(mainNavStore, (state) => state.context.stage);
}

export function useMainNavWidth(): number {
  return useSelector(mainNavStore, (state) => state.context.rawWidth);
}

// ADR Hooks (re-exported from module)
export {
  useAdrs,
  useAdrById,
  useAdrsForBoard,
  useAdrsForCanvas,
  useAdrActions,
} from '../adr/hooks';

// Resource Hooks (re-exported from module)
export {
  useResources,
  useResourceById,
  useResourcesForBoard,
  useResourcesForCanvas,
  useResourceActions,
} from '../resources/hooks';
